import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NavMenu from '../components/ui/nav-menu';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import MicrophoneHelp, { MicrophoneHelpLink } from '../components/ui/microphone-help';
import { TaskListSkeleton } from '../components/ui/task-skeleton';
import { 
  ArrowLeft, Send, Loader2, Mic, MicOff, Camera, Paperclip, X, 
  ChevronDown, ChevronUp, Plus, MapPin, Clock, AlertCircle, Calendar,
  Users, Briefcase, Building2, DollarSign, Trophy, Truck, Banknote,
  UserPlus, ClipboardCheck, FileText, Search, ListFilter, Inbox, Hourglass, CheckCircle2, ChevronRight, Check, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper for time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const UnifiedCapture = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const carouselRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Determine initial module based on route
  const getInitialModule = () => {
    if (location.pathname.includes('leads-referral') || location.pathname.includes('lead-capture')) return 0;
    if (location.pathname.includes('log-update')) return 1;
    if (location.pathname.includes('claim')) return 2;
    return 1; // Default to Site Update
  };
  
  const [activeModule, setActiveModule] = useState(getInitialModule());
  const [activeScreen, setActiveScreen] = useState(0); // 0=Input, 1=Tasks
  const [counts, setCounts] = useState({ leads: 0, updates: 0, claims: 0 });
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [micError, setMicError] = useState('');
  const recognitionRef = useRef(null);

  // Update activeModule when route changes (footer navigation)
  useEffect(() => {
    const newModule = getInitialModule();
    if (newModule !== activeModule) {
      setActiveModule(newModule);
      setActiveScreen(0); // Reset to Input screen when switching modules
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle navigation from sub-modules with scrollToTasks state
  useEffect(() => {
    if (location.state?.scrollToTasks) {
      // Small delay to ensure component is mounted
      setTimeout(() => {
        scrollToScreen(1);
      }, 100);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Form states for each module
  const [leadForm, setLeadForm] = useState({
    raw_notes: '', entry_type: '', work_type: '', source: '',
    main_contact: '', site_location: '', phone: '', email: '',
    urgency: '', follow_up_by: '', est_value: '', referral_approach: '', referral_notes: '', project_notes: ''
  });
  const [updateForm, setUpdateForm] = useState({
    job_name: '', update_text: '', site_address: '', category: '', urgency: '', required_by: ''
  });
  const [claimForm, setClaimForm] = useState({
    claimText: '', jobReference: '', clientName: '', dueDate: '', urgency: '', estimatedAmount: '', notes: ''
  });
  
  const [attachments, setAttachments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [showUpdateDetails, setShowUpdateDetails] = useState(false);
  const [showClaimDetails, setShowClaimDetails] = useState(false);

  // Tasks section state
  const [leads, setLeads] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [markingDone, setMarkingDone] = useState(null);
  const [visibleLeadsCount, setVisibleLeadsCount] = useState(5);
  const [visibleUpdatesCount, setVisibleUpdatesCount] = useState(5);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const modules = [
    {
      id: 'lead',
      title: 'Lead / Referral',
      stage: 'WIN WORK',
      intro: 'Capture leads and referrals fast.',
      placeholder: "What's the opportunity?",
      stageColor: 'text-emerald-400',
      barColor: 'bg-emerald-500',
      micColor: 'bg-emerald-600 border-emerald-400',
      buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
      accentColor: 'text-emerald-600',
      backPath: '/tradie/win',
      stageIcon: Trophy,
      moduleIcon: UserPlus
    },
    {
      id: 'update',
      title: 'Site Update',
      stage: 'DELIVER WORK',
      intro: 'Capture site progress fast.',
      placeholder: "What's happening on site?",
      stageColor: 'text-blue-400',
      barColor: 'bg-blue-500',
      micColor: 'bg-blue-600 border-blue-400',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      accentColor: 'text-blue-600',
      backPath: '/tradie/deliver',
      stageIcon: Truck,
      moduleIcon: ClipboardCheck
    },
    {
      id: 'claim',
      title: 'Claim / Invoice',
      stage: 'GET PAID',
      intro: 'Capture what needs to be claimed.',
      placeholder: "What needs to be claimed?",
      stageColor: 'text-orange-400',
      barColor: 'bg-orange-500',
      micColor: 'bg-orange-600 border-orange-400',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
      accentColor: 'text-orange-600',
      backPath: '/tradie/get-paid',
      stageIcon: Banknote,
      moduleIcon: FileText
    }
  ];

  // Options
  const entryTypes = [
    { value: 'lead', label: 'Job Lead' },
    { value: 'referral_received', label: 'Referral Received' },
    { value: 'referral_given', label: 'Referral Given' }
  ];
  const workTypes = [
    { value: 'solar', label: 'Solar' }, { value: 'electrical', label: 'Electrical' },
    { value: 'rendering', label: 'Rendering' }, { value: 'maintenance', label: 'Maintenance' },
    { value: 'plumbing', label: 'Plumbing' }, { value: 'fitout', label: 'Fitout' },
    { value: 'other', label: 'Other' }
  ];
  const sources = [
    { value: 'existing_builder', label: 'Existing Builder' }, { value: 'existing_client', label: 'Existing Client' },
    { value: 'direct_enquiry', label: 'Direct Enquiry' }, { value: 'referral', label: 'Referral' },
    { value: 'partner_introducer', label: 'Partner / Introducer' }, { value: 'other', label: 'Other' }
  ];
  const categories = [
    { value: 'progress', label: 'Progress' }, { value: 'issue', label: 'Issue' },
    { value: 'material', label: 'Material' }, { value: 'safety', label: 'Safety' },
    { value: 'variation', label: 'Variation' }, { value: 'general', label: 'General' }
  ];
  const urgencies = [
    { value: 'Low', label: 'Low' }, { value: 'Medium', label: 'Medium' }, { value: 'High', label: 'High' }
  ];

  useEffect(() => {
    fetchCounts();
    fetchTasks();
    initSpeechRecognition();
    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, []);

  useEffect(() => {
    // Always start on Input screen (screen 0)
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
  }, []);

  const fetchTasks = async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true);
    try {
      const [updatesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/updates/my`),
        axios.get(`${API_URL}/leads/my`)
      ]);
      setUpdates(updatesRes.data);
      setLeads(leadsRes.data);
      if (isRefresh) toast.success('Refreshed!');
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      if (isRefresh) toast.error('Failed to refresh');
    } finally {
      setTasksLoading(false);
      setIsRefreshing(false);
    }
  };

  // Pull to refresh handler
  const handleRefresh = useCallback(() => {
    if (!isRefreshing) {
      fetchTasks(true);
    }
  }, [isRefreshing]);

  // Filtered data for tasks section
  const filteredLeads = useMemo(() => {
    let result = leads;
    result = result.filter(lead => {
      const status = (lead.status || 'New').toLowerCase();
      const isDone = status === 'done' || status === 'won' || status === 'lost';
      if (statusFilter === 'all') return !isDone;
      if (statusFilter === 'new') return status === 'new';
      if (statusFilter === 'in-progress') return status === 'in progress' || status === 'in-progress';
      if (statusFilter === 'done') return isDone;
      return !isDone;
    });
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead => 
        (lead.summary_text && lead.summary_text.toLowerCase().includes(query)) ||
        (lead.client_name && lead.client_name.toLowerCase().includes(query)) ||
        (lead.site_location && lead.site_location.toLowerCase().includes(query)) ||
        (lead.reference_id && lead.reference_id.toLowerCase().includes(query))
      );
    }
    return result;
  }, [leads, searchQuery, statusFilter]);

  const filteredUpdates = useMemo(() => {
    let result = updates;
    result = result.filter(update => {
      const status = (update.status || 'New').toLowerCase();
      const isDone = status === 'done';
      if (statusFilter === 'all') return !isDone;
      if (statusFilter === 'new') return status === 'new';
      if (statusFilter === 'in-progress') return status === 'in progress' || status === 'in-progress';
      if (statusFilter === 'done') return isDone;
      return !isDone;
    });
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(update => 
        (update.update_text && update.update_text.toLowerCase().includes(query)) ||
        (update.job_name && update.job_name.toLowerCase().includes(query)) ||
        (update.site_address && update.site_address.toLowerCase().includes(query))
      );
    }
    return result;
  }, [updates, searchQuery, statusFilter]);

  const handleMarkDone = async (e, type, id) => {
    e.stopPropagation();
    setMarkingDone(id);
    try {
      if (type === 'lead') {
        await axios.patch(`${API_URL}/leads/${id}`, { status: 'Done' });
        setLeads(prev => prev.map(l => l.id === id ? { ...l, status: 'Done' } : l));
      } else {
        await axios.put(`${API_URL}/updates/${id}`, { status: 'Done' });
        setUpdates(prev => prev.map(u => u.id === id ? { ...u, status: 'Done' } : u));
      }
      toast.success('Marked as Done!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setMarkingDone(null);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-amber-100 text-amber-700';
      case 'Done': case 'Won': return 'bg-green-100 text-green-700';
      case 'Lost': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const fetchCounts = async () => {
    try {
      const [updatesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/updates/my`),
        axios.get(`${API_URL}/leads/my`)
      ]);
      setCounts({ leads: leadsRes.data.length, updates: updatesRes.data.length, claims: 0 });
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-AU';
      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript + ' ';
        }
        if (finalTranscript) {
          if (activeModule === 0) setLeadForm(prev => ({ ...prev, raw_notes: prev.raw_notes + finalTranscript }));
          else if (activeModule === 1) setUpdateForm(prev => ({ ...prev, update_text: prev.update_text + finalTranscript }));
          else setClaimForm(prev => ({ ...prev, claimText: prev.claimText + finalTranscript }));
        }
      };
      recognition.onerror = (event) => {
        setIsListening(false);
        if (event.error === 'not-allowed') setMicError('Microphone blocked.');
        else if (event.error !== 'no-speech') setMicError('Could not access microphone.');
      };
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  };

  const toggleListening = async () => {
    setMicError('');
    if (!speechSupported) { setMicError('Speech not supported.'); return; }
    if (isListening) { recognitionRef.current.stop(); setIsListening(false); }
    else {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        recognitionRef.current.start(); setIsListening(true);
      } catch (error) {
        setMicError(error.name === 'NotAllowedError' ? 'Microphone blocked.' : 'Could not start microphone.');
      }
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollPos = carouselRef.current.scrollLeft;
      const containerWidth = carouselRef.current.offsetWidth;
      const newActive = Math.round(scrollPos / containerWidth);
      if (newActive !== activeScreen) {
        setActiveScreen(Math.max(0, Math.min(1, newActive)));
        if (isListening) { recognitionRef.current.stop(); setIsListening(false); }
      }
    }
  };

  const scrollToScreen = (index) => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({ left: index * containerWidth, behavior: 'smooth' });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (activeModule === 1) {
          setPhotos(prev => [...prev, { name: file.name, data: ev.target.result }]);
        } else {
          setAttachments(prev => [...prev, { name: file.name, data: ev.target.result }]);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    if (activeModule === 1) setPhotos(prev => prev.filter((_, i) => i !== index));
    else setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    if (!leadForm.raw_notes.trim()) { 
      setFormErrors({ raw_notes: 'Please describe the opportunity' });
      toast.error('Please describe the opportunity'); 
      return; 
    }
    setLoading(true);
    try {
      const payload = { ...leadForm, attachments: attachments.map(a => a.data) };
      await axios.post(`${API_URL}/leads/`, payload);
      toast.success('Lead captured!');
      navigate('/tradie/win');
    } catch (error) {
      toast.error('Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    if (!updateForm.update_text.trim()) { 
      setFormErrors({ update_text: 'Please describe the update' });
      toast.error('Please describe the update'); 
      return; 
    }
    setLoading(true);
    try {
      const payload = { ...updateForm, photos: photos.map(p => p.data) };
      await axios.post(`${API_URL}/updates/`, payload);
      toast.success('Update saved!');
      navigate('/tradie/deliver');
    } catch (error) {
      toast.error('Failed to save update');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    if (!claimForm.claimText.trim()) { 
      setFormErrors({ claimText: 'Please describe the claim' });
      toast.error('Please describe the claim'); 
      return; 
    }
    setLoading(true);
    try {
      toast.success('Claim captured!');
      navigate('/tradie/get-paid');
    } catch (error) {
      toast.error('Failed to save claim');
    } finally {
      setLoading(false);
    }
  };

  const activeModuleData = modules[activeModule];

  // Function to navigate to Tasks screen
  const goToTasks = () => {
    scrollToScreen(1);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Dynamic Header with personalized greeting */}
      <header className="bg-[#1F2A37] text-white px-4 py-3 sticky top-0 z-20 relative">
        <div className={`h-1 ${activeModuleData.barColor} absolute top-0 left-0 right-0 transition-colors duration-300`}></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeModuleData.stageIcon && (
              <activeModuleData.stageIcon className={`w-7 h-7 ${activeModuleData.stageColor}`} strokeWidth={1.5} />
            )}
            <div>
              <h1 className="text-xl font-bold">{getGreeting()}, {user?.name?.split(' ')[0] || 'there'}</h1>
              <p className={`${activeModuleData.stageColor} text-xs font-semibold uppercase tracking-wide`}>{activeModuleData.stage}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tasks icon - navigates to Tasks screen */}
            <button
              onClick={goToTasks}
              data-testid="header-tasks-btn"
              className={`p-2 rounded-lg hover:bg-slate-700 transition-colors relative`}
              title="View Tasks"
            >
              <Inbox className={`w-5 h-5 ${activeModuleData.stageColor}`} />
              {/* Badge showing count */}
              {(activeModule === 0 ? filteredLeads.length : activeModule === 1 ? filteredUpdates.length : 0) > 0 && (
                <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center ${
                  activeModule === 0 ? 'bg-emerald-500' : activeModule === 1 ? 'bg-blue-500' : 'bg-orange-500'
                } text-white`}>
                  {activeModule === 0 ? filteredLeads.length : activeModule === 1 ? filteredUpdates.length : 0}
                </span>
              )}
            </button>
            <NavMenu />
          </div>
        </div>
      </header>

      {/* ModuleRail */}
      <ModuleRail 
        stage={activeModule === 0 ? 'win' : activeModule === 1 ? 'deliver' : 'get-paid'} 
        activeModule={modules[activeModule]?.id} 
      />

      {/* Swipeable Input / Tasks Screens */}
      <div 
        ref={carouselRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide flex-1"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {/* SCREEN 1: INPUT FORM */}
        <div className="flex-shrink-0 w-full snap-center flex flex-col" style={{ minWidth: '100%' }}>
          <main className="p-4 max-w-lg mx-auto w-full">
            <Card className="border-2 border-slate-200">
              <CardContent className="p-4 space-y-4">
                {/* Conditional form based on activeModule */}
                {activeModule === 0 && (
                  <form onSubmit={handleLeadSubmit} className="space-y-4">
                    <p className="text-slate-600 text-base">{modules[0].intro}</p>
                    <div className="text-center py-1">
                      <button type="button" onClick={toggleListening} className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full transition-all border-4 shadow-lg ${isListening ? 'bg-red-500 border-red-300 animate-pulse' : modules[0].micColor} active:scale-95`}>
                        {isListening ? <MicOff className="w-8 h-8 text-white" /> : <Mic className="w-8 h-8 text-white" />}
                      </button>
                      <p className="text-slate-600 text-sm font-medium mt-2">{isListening ? <span className="text-red-600">Listening...</span> : 'Speak or type'}</p>
                      <MicrophoneHelpLink className="mt-0" />
                      <MicrophoneHelp error={micError} className="mt-0.5" />
                    </div>
                    <Textarea placeholder={modules[0].placeholder} value={leadForm.raw_notes} onChange={(e) => { setLeadForm(prev => ({ ...prev, raw_notes: e.target.value })); setFormErrors({}); }} className={`min-h-[80px] text-base border-2 ${formErrors.raw_notes ? 'border-red-500 focus:ring-red-500' : ''}`} />
                    {formErrors.raw_notes && <p className="text-red-500 text-sm -mt-2">{formErrors.raw_notes}</p>}
                    <div className="flex items-center justify-end gap-2">
                      <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" onChange={handleFileSelect} className="hidden" />
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"><Camera className="w-4 h-4" /> Photo</button>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"><Paperclip className="w-4 h-4" /> Attach</button>
                    </div>
                    {attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {attachments.map((a, i) => (
                          <div key={i} className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded text-sm">
                            <span className="truncate max-w-[100px]">{a.name}</span>
                            <button type="button" onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider pt-2 border-t">ADD DETAILS (OPTIONAL)</p>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" /> Lead or Referral?</Label>
                        <Select value={leadForm.entry_type} onValueChange={(v) => setLeadForm(prev => ({ ...prev, entry_type: v }))}>
                          <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Choose type" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lead_new">New Lead</SelectItem>
                            <SelectItem value="lead_existing">Existing Client</SelectItem>
                            <SelectItem value="referral_given">Referral Given</SelectItem>
                            <SelectItem value="referral_received">Referral Received</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Briefcase className="w-4 h-4 text-emerald-600" /> Work type</Label>
                        <Select value={leadForm.work_type} onValueChange={(v) => setLeadForm(prev => ({ ...prev, work_type: v }))}>
                          <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Type of work" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residential">Residential</SelectItem>
                            <SelectItem value="commercial">Commercial</SelectItem>
                            <SelectItem value="maintenance">Maintenance</SelectItem>
                            <SelectItem value="renovation">Renovation</SelectItem>
                            <SelectItem value="new_build">New Build</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm font-bold text-slate-700">Source</Label>
                        <Select value={leadForm.source} onValueChange={(v) => setLeadForm(prev => ({ ...prev, source: v }))}>
                          <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Where did this come from?" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="word_of_mouth">Word of Mouth</SelectItem>
                            <SelectItem value="social_media">Social Media</SelectItem>
                            <SelectItem value="google">Google</SelectItem>
                            <SelectItem value="hipages">HiPages</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="repeat_client">Repeat Client</SelectItem>
                            <SelectItem value="trade_referral">Trade Referral</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowLeadDetails(!showLeadDetails)} className={`w-full flex items-center justify-between p-3 rounded-xl text-left text-emerald-600 bg-emerald-50 font-medium text-sm border-2 border-emerald-200`}>
                      <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add contact details to improve follow-up</span>
                      {showLeadDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {showLeadDetails && (
                      <div className="space-y-4 p-4 bg-white rounded-xl border-2 border-emerald-200">
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-600" /> Site location</Label>
                          <Input placeholder="Site address" value={leadForm.site_location} onChange={(e) => setLeadForm(prev => ({ ...prev, site_location: e.target.value }))} className="h-11 border-2" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" /> Contact name</Label>
                          <Input placeholder="Main contact person" value={leadForm.main_contact} onChange={(e) => setLeadForm(prev => ({ ...prev, main_contact: e.target.value }))} className="h-11 border-2" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Phone</Label>
                            <Input type="tel" placeholder="Phone" value={leadForm.phone} onChange={(e) => setLeadForm(prev => ({ ...prev, phone: e.target.value }))} className="h-11 border-2" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Email</Label>
                            <Input type="email" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))} className="h-11 border-2" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Urgency</Label>
                            <Select value={leadForm.urgency} onValueChange={(v) => setLeadForm(prev => ({ ...prev, urgency: v }))}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Timing" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="hot">Hot - This Week</SelectItem>
                                <SelectItem value="warm">Warm - This Month</SelectItem>
                                <SelectItem value="cold">Cold - Future</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Est. value</Label>
                            <Input type="text" placeholder="$" value={leadForm.est_value} onChange={(e) => setLeadForm(prev => ({ ...prev, est_value: e.target.value }))} className="h-11 border-2" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-600" /> Follow-up by</Label>
                          <Input type="date" value={leadForm.follow_up_by} onChange={(e) => setLeadForm(prev => ({ ...prev, follow_up_by: e.target.value }))} className="h-11 border-2" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700">Project notes</Label>
                          <Textarea placeholder="Additional details..." value={leadForm.project_notes} onChange={(e) => setLeadForm(prev => ({ ...prev, project_notes: e.target.value }))} className="min-h-[60px] border-2" />
                        </div>
                      </div>
                    )}
                    <Button type="submit" disabled={loading} className={`w-full h-12 ${modules[0].buttonColor} text-white text-base font-bold`}>{loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating...</> : <><Send className="w-5 h-5 mr-2" />Generate Summary</>}</Button>
                  </form>
                )}
                
                {activeModule === 1 && (
                  <form onSubmit={handleUpdateSubmit} className="space-y-4">
                    <p className="text-slate-600 text-base">{modules[1].intro}</p>
                    <div className="text-center py-1">
                      <button type="button" onClick={toggleListening} className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full transition-all border-4 shadow-lg ${isListening ? 'bg-red-500 border-red-300 animate-pulse' : modules[1].micColor} active:scale-95`}>
                        {isListening ? <MicOff className="w-8 h-8 text-white" /> : <Mic className="w-8 h-8 text-white" />}
                      </button>
                      <p className="text-slate-600 text-sm font-medium mt-2">{isListening ? <span className="text-red-600">Listening...</span> : 'Speak or type'}</p>
                      <MicrophoneHelpLink className="mt-0" />
                      <MicrophoneHelp error={micError} className="mt-0.5" />
                    </div>
                    <Textarea placeholder={modules[1].placeholder} value={updateForm.update_text} onChange={(e) => { setUpdateForm(prev => ({ ...prev, update_text: e.target.value })); setFormErrors({}); }} className={`min-h-[80px] text-base border-2 ${formErrors.update_text ? 'border-red-500 focus:ring-red-500' : ''}`} />
                    {formErrors.update_text && <p className="text-red-500 text-sm -mt-2">{formErrors.update_text}</p>}
                    <div className="flex items-center justify-end gap-2">
                      <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" onChange={handleFileSelect} className="hidden" />
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"><Camera className="w-4 h-4" /> Photo</button>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"><Paperclip className="w-4 h-4" /> Attach</button>
                    </div>
                    {photos.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {photos.map((p, i) => (
                          <div key={i} className="relative w-16 h-16 rounded overflow-hidden">
                            <img src={p.data} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider pt-2 border-t">ADD DETAILS (OPTIONAL)</p>
                    <div className="space-y-1">
                      <Label className="text-sm font-bold text-slate-700">Job name</Label>
                      <Input placeholder="Project or site name" value={updateForm.job_name} onChange={(e) => setUpdateForm(prev => ({ ...prev, job_name: e.target.value }))} className="h-11 border-2" />
                    </div>
                    <button type="button" onClick={() => setShowUpdateDetails(!showUpdateDetails)} className="w-full flex items-center justify-between p-3 rounded-xl text-left text-blue-600 bg-blue-50 font-medium text-sm border-2 border-blue-200">
                      <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add more details to improve follow-up</span>
                      {showUpdateDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {showUpdateDetails && (
                      <div className="space-y-4 p-4 bg-white rounded-xl border-2 border-blue-200">
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" /> Site address</Label>
                          <Input placeholder="Where is this job?" value={updateForm.site_address} onChange={(e) => setUpdateForm(prev => ({ ...prev, site_address: e.target.value }))} className="h-11 border-2" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Category</Label>
                            <Select value={updateForm.category} onValueChange={(v) => setUpdateForm(prev => ({ ...prev, category: v }))}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Category" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="progress">Progress</SelectItem>
                                <SelectItem value="issue">Issue</SelectItem>
                                <SelectItem value="delay">Delay</SelectItem>
                                <SelectItem value="completion">Completion</SelectItem>
                                <SelectItem value="labour">Labour</SelectItem>
                                <SelectItem value="materials">Materials</SelectItem>
                                <SelectItem value="variation">Variation</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Urgency</Label>
                            <Select value={updateForm.urgency} onValueChange={(v) => setUpdateForm(prev => ({ ...prev, urgency: v }))}>
                              <SelectTrigger className="h-11 border-2"><SelectValue placeholder="Level" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Low">Low</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="High">High</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> Required by</Label>
                          <Input type="date" value={updateForm.required_by} onChange={(e) => setUpdateForm(prev => ({ ...prev, required_by: e.target.value }))} className="h-11 border-2" />
                        </div>
                      </div>
                    )}
                    <Button type="submit" disabled={loading} className={`w-full h-12 ${modules[1].buttonColor} text-white text-base font-bold`}>{loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</> : <><Send className="w-5 h-5 mr-2" />Submit Update</>}</Button>
                  </form>
                )}
                
                {activeModule === 2 && (
                  <form onSubmit={handleClaimSubmit} className="space-y-4">
                    <p className="text-slate-600 text-base">{modules[2].intro}</p>
                    <div className="text-center py-1">
                      <button type="button" onClick={toggleListening} className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full transition-all border-4 shadow-lg ${isListening ? 'bg-red-500 border-red-300 animate-pulse' : modules[2].micColor} active:scale-95`}>
                        {isListening ? <MicOff className="w-8 h-8 text-white" /> : <Mic className="w-8 h-8 text-white" />}
                      </button>
                      <p className="text-slate-600 text-sm font-medium mt-2">{isListening ? <span className="text-red-600">Listening...</span> : 'Speak or type'}</p>
                      <MicrophoneHelpLink className="mt-0" />
                      <MicrophoneHelp error={micError} className="mt-0.5" />
                    </div>
                    <Textarea placeholder={modules[2].placeholder} value={claimForm.claimText} onChange={(e) => { setClaimForm(prev => ({ ...prev, claimText: e.target.value })); setFormErrors({}); }} className={`min-h-[80px] text-base border-2 ${formErrors.claimText ? 'border-red-500 focus:ring-red-500' : ''}`} />
                    {formErrors.claimText && <p className="text-red-500 text-sm -mt-2">{formErrors.claimText}</p>}
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider pt-2 border-t">ADD DETAILS (OPTIONAL)</p>
                    <div className="space-y-1">
                      <Label className="text-sm font-bold text-slate-700">Job reference</Label>
                      <Input placeholder="Job or project name" value={claimForm.jobReference} onChange={(e) => setClaimForm(prev => ({ ...prev, jobReference: e.target.value }))} className="h-11 border-2" />
                    </div>
                    <button type="button" onClick={() => setShowClaimDetails(!showClaimDetails)} className="w-full flex items-center justify-between p-3 rounded-xl text-left text-orange-600 bg-orange-50 font-medium text-sm border-2 border-orange-200">
                      <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add more details to improve follow-up</span>
                      {showClaimDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {showClaimDetails && (
                      <div className="space-y-4 p-4 bg-white rounded-xl border-2 border-orange-200">
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700">Client name</Label>
                          <Input placeholder="Who to bill?" value={claimForm.clientName} onChange={(e) => setClaimForm(prev => ({ ...prev, clientName: e.target.value }))} className="h-11 border-2" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Due date</Label>
                            <Input type="date" value={claimForm.dueDate} onChange={(e) => setClaimForm(prev => ({ ...prev, dueDate: e.target.value }))} className="h-11 border-2" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-slate-700">Amount</Label>
                            <Input type="text" placeholder="$" value={claimForm.estimatedAmount} onChange={(e) => setClaimForm(prev => ({ ...prev, estimatedAmount: e.target.value }))} className="h-11 border-2" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-bold text-slate-700">Notes</Label>
                          <Textarea placeholder="Additional details..." value={claimForm.notes} onChange={(e) => setClaimForm(prev => ({ ...prev, notes: e.target.value }))} className="min-h-[60px] border-2" />
                        </div>
                      </div>
                    )}
                    <Button type="submit" disabled={loading} className={`w-full h-12 ${modules[2].buttonColor} text-white text-base font-bold`}>{loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating...</> : <><Send className="w-5 h-5 mr-2" />Create Claim</>}</Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </main>
        </div>

        {/* SCREEN 2: TASKS */}
        <div className="flex-shrink-0 w-full snap-center" style={{ minWidth: '100%' }}>
          <div className="p-4 max-w-lg mx-auto">
            {/* Pull to Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`w-full mb-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                isRefreshing 
                  ? 'bg-slate-100 text-slate-400' 
                  : activeModule === 0 
                    ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' 
                    : activeModule === 1 
                      ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                      : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
            </button>

            {/* Search & Filters Card */}
            <Card className="border-2 border-slate-200 shadow-sm mb-4">
              <CardContent className="p-4">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={activeModule === 0 ? "Search leads, referrals..." : "Search updates..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11 border-2 border-slate-200 rounded-xl bg-slate-50"
                  />
                </div>
                <div className={`flex rounded-lg p-1 ${
                  activeModule === 0 ? 'bg-emerald-50' : activeModule === 1 ? 'bg-blue-50' : 'bg-orange-50'
                }`}>
                  {[
                    { id: 'all', label: 'All Open', icon: ListFilter },
                    { id: 'new', label: 'New', icon: Inbox },
                    { id: 'in-progress', label: 'In Progress', icon: Hourglass },
                    { id: 'done', label: 'Done', icon: CheckCircle2 }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setStatusFilter(tab.id);
                        setVisibleLeadsCount(5);
                        setVisibleUpdatesCount(5);
                      }}
                      className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                        statusFilter === tab.id
                          ? `bg-white shadow-sm ${activeModule === 0 ? 'text-emerald-600 border-b-2 border-emerald-500' : activeModule === 1 ? 'text-blue-600 border-b-2 border-blue-500' : 'text-orange-600 border-b-2 border-orange-500'}`
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Task Cards */}
            <div className="space-y-3">
              {tasksLoading ? (
                <TaskListSkeleton count={5} color={activeModule === 0 ? 'emerald' : activeModule === 1 ? 'blue' : 'orange'} />
              ) : activeModule === 0 ? (
                filteredLeads.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No leads found</p>
                  </div>
                ) : (
                  <>
                    {filteredLeads.slice(0, visibleLeadsCount).map((lead) => (
                    <Card
                      key={lead.id}
                      onClick={() => navigate(`/tradie/lead/${lead.id}`)}
                      className="p-4 border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-emerald-600" />
                          <span className="font-semibold text-slate-800">{lead.entry_type_display || 'Lead'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(lead.status || 'New')}`}>
                            {lead.status || 'New'}
                          </span>
                          <span className="text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                            #{lead.reference_id}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{lead.summary_text}</p>
                      {lead.site_location && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {lead.site_location}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-400">{format(new Date(lead.created_at), 'MMM d, h:mm a')}</span>
                        <div className="flex items-center gap-2">
                          {(lead.status !== 'Done' && lead.status !== 'Won') && (
                            <button
                              onClick={(e) => handleMarkDone(e, 'lead', lead.id)}
                              disabled={markingDone === lead.id}
                              className="flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-medium"
                            >
                              {markingDone === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Done
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </Card>
                    ))}
                    {filteredLeads.length > visibleLeadsCount && (
                      <button
                        onClick={() => setVisibleLeadsCount(prev => prev + 5)}
                        className="w-full py-3 text-emerald-600 font-medium text-sm bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                      >
                        Show More ({filteredLeads.length - visibleLeadsCount} remaining)
                      </button>
                    )}
                  </>
                )
              ) : activeModule === 1 ? (
                filteredUpdates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No updates found</p>
                  </div>
                ) : (
                  <>
                    {filteredUpdates.slice(0, visibleUpdatesCount).map((update) => (
                    <Card
                      key={update.id}
                      onClick={() => navigate(`/tradie/update/${update.id}`)}
                      className="p-4 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold text-slate-800">{update.job_name || 'Site Update'}</span>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(update.status || 'New')}`}>
                          {update.status || 'New'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{update.update_text}</p>
                      {update.site_address && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {update.site_address}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-400">{format(new Date(update.created_at), 'MMM d, h:mm a')}</span>
                        <div className="flex items-center gap-2">
                          {update.status !== 'Done' && (
                            <button
                              onClick={(e) => handleMarkDone(e, 'update', update.id)}
                              disabled={markingDone === update.id}
                              className="flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-medium"
                            >
                              {markingDone === update.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Done
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </Card>
                    ))}
                    {filteredUpdates.length > visibleUpdatesCount && (
                      <button
                        onClick={() => setVisibleUpdatesCount(prev => prev + 5)}
                        className="w-full py-3 text-blue-600 font-medium text-sm bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        Show More ({filteredUpdates.length - visibleUpdatesCount} remaining)
                      </button>
                    )}
                  </>
                )
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Claims feature coming soon</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Screen Indicator Dots */}
      <div className="flex items-center justify-center gap-2 py-2 bg-slate-50">
        {[0, 1].map((index) => (
          <button
            key={index}
            onClick={() => scrollToScreen(index)}
            className={`h-2 rounded-full transition-all ${
              activeScreen === index 
                ? `w-6 ${activeModuleData.barColor}` 
                : 'w-2 bg-slate-300'
            }`}
          />
        ))}
      </div>

      <FooterNav counts={counts} />
    </div>
  );
};

export default UnifiedCapture;
